import React, { useState, useMemo } from 'react';
import { getScenarioById, getRandomScenario } from '../data/roleplayData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const RoleplayScenarioPage = ({ scenarioId, language = 'traditional', onBack, onStartRoleplay }) => {
  const [isStarting, setIsStarting] = useState(false);

  // Load specific scenario if present
  const { data: remote } = useSupabaseData({
    table: 'eng_roleplay',
    mapRow: (row) => row,
    getFallback: () => null,
    query: {
      // eq: scenarioId ? { scenario_id: scenarioId } : undefined
    }
  });

  const scenario = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) {
      return scenarioId ? getScenarioById(scenarioId) : getRandomScenario();
    }
    const rows = Array.isArray(remote) ? remote : [remote];
    // Prefer a row matching type 'scenario' or a specific id
    const r = rows.find(x => (x.type || x.section) === 'scenario' && (!scenarioId || x.scenario_id === scenarioId || x.id === scenarioId)) || rows[0];
    if (!r) return scenarioId ? getScenarioById(scenarioId) : getRandomScenario();
    return {
      id: r.scenario_id || r.id,
      icon: r.icon || '🎭',
      title: { tc: r.title_tc || '', sc: r.title_sc || '', en: r.title_en || '' },
      description: { tc: r.description_tc || '', sc: r.description_sc || '', en: r.description_en || '' },
      duration: { tc: r.duration_tc || '10 分鐘', sc: r.duration_sc || '10 分钟', en: r.duration_en || '10 min' },
      participants: r.participants || 2,
      difficultyColor: r.difficulty_color || 'blue',
      difficulty: { tc: r.difficulty_tc || '中級', sc: r.difficulty_sc || '中级', en: r.difficulty_en || 'Intermediate' }
    };
  }, [remote, scenarioId]);

  const getText = (textObj) => {
    return textObj[language] || textObj.traditional;
  };

  const handleStartRoleplay = () => {
    setIsStarting(true);
    setTimeout(() => {
      onStartRoleplay(scenario.id);
    }, 1000);
  };

  if (!scenario) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
        <div className="px-6 py-8">
          <button onClick={onBack} className="text-blue-400 mb-4">
            ← 返回
          </button>
          <div className="text-center py-8">
            <p>場景未找到</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-white">
            ←
          </button>
          <h1 className="text-xl font-bold">
            {getText(scenario.title)}
          </h1>
          <div className="w-8"></div> {/* Spacer for centering */}
        </div>

        {/* Scenario Info Card */}
        <div className="bg-gray-800/30 border border-gray-600 rounded-xl p-6 mb-6">
          <div className="flex items-center space-x-4 mb-4">
            <div className="text-4xl">
              {scenario.icon}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold mb-1">
                {getText(scenario.title)}
              </h2>
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <span>{getText(scenario.duration)}</span>
                <span>•</span>
                <span>{scenario.participants} participants</span>
              </div>
            </div>
            <div className={`px-3 py-1 rounded text-sm font-medium text-white ${
              scenario.difficultyColor === 'orange' ? 'bg-orange-500' :
              scenario.difficultyColor === 'green' ? 'bg-green-500' :
              scenario.difficultyColor === 'blue' ? 'bg-blue-500' :
              'bg-gray-500'
            }`}>
              {getText(scenario.difficulty)}
            </div>
          </div>

          <p className="text-gray-300 mb-6">
            {getText(scenario.description)}
          </p>

          {/* Start Button */}
          <button
            onClick={handleStartRoleplay}
            disabled={isStarting}
            className={`w-full py-4 rounded-xl font-semibold text-lg transition-all ${
              isStarting
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-500 to-blue-500 text-white hover:from-green-600 hover:to-blue-600'
            }`}
          >
            {isStarting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {language === 'traditional' ? '準備中...' :
                 language === 'simplified' ? '准备中...' :
                 'Preparing...'}
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <span className="mr-2">🎭</span>
                {language === 'traditional' ? '開始角色扮演' :
                 language === 'simplified' ? '开始角色扮演' :
                 'Start Roleplay'}
              </div>
            )}
          </button>
        </div>

        {/* Tips Section */}
        <div className="bg-gray-800/30 border border-gray-600 rounded-xl p-4">
          <h3 className="font-semibold mb-3">
            {language === 'traditional' ? '💡 學習提示' :
             language === 'simplified' ? '💡 学习提示' :
             '💡 Learning Tips'}
          </h3>
          <ul className="text-gray-300 text-sm space-y-2">
            <li>• {language === 'traditional' ? '仔細聆聽對話夥伴的發音和語調' :
                     language === 'simplified' ? '仔细聆听对话伙伴的发音和语调' :
                     'Listen carefully to your conversation partner\'s pronunciation and intonation'}</li>
            <li>• {language === 'traditional' ? '嘗試使用提供的關鍵詞彙和短語' :
                     language === 'simplified' ? '尝试使用提供的关键词汇和短语' :
                     'Try to use the provided key vocabulary and phrases'}</li>
            <li>• {language === 'traditional' ? '保持自然的對話節奏和流暢度' :
                     language === 'simplified' ? '保持自然的对话节奏和流畅度' :
                     'Maintain natural conversation rhythm and fluency'}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RoleplayScenarioPage;
